import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Baby, User, Heart, Plane, Stethoscope } from 'lucide-react';
import { useSiteContent } from '../../lib/siteContent';

const ICONS = {
  paediatric: Baby,
  adults: User,
  elderly: Heart,
  travel: Plane,
  other: Stethoscope,
};

function examplesText(examples) {
  if (Array.isArray(examples)) return examples.filter(Boolean).join(" · ");
  return examples || "";
}

export default function CatalogueTeaser() {
  const site = useSiteContent();
  const catalogue = site.catalogue || {};
  const categories = Array.isArray(catalogue.categories) ? catalogue.categories.filter((c) => c && c.title) : [];

  return (
    <section
      id="services"
      data-testid="catalogue-teaser-section"
      className="bg-[#fbfaf7] py-20 md:py-24"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#f37721]" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            {catalogue.eyebrow}
          </span>
          <h2
            className="text-3xl sm:text-4xl font-bold tracking-tight text-[#311b52] mt-4"
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            {catalogue.headline}
          </h2>
          <p className="text-base text-[#3e195e]/70 mt-4 max-w-2xl mx-auto" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            {catalogue.body}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5 mb-12">
          {categories.map((cat, i) => {
            const Icon = ICONS[cat.id] || Stethoscope;
            return (
              <motion.div
                key={cat.id || cat.title || i}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
                data-testid={`catalogue-teaser-card-${cat.id || i}`}
                className="bg-white rounded-2xl p-6 border border-[#673c96]/8 shadow-[0_4px_18px_rgb(0,0,0,0.03)] hover:shadow-[0_10px_28px_rgb(0,0,0,0.06)] hover:-translate-y-0.5 transition-all duration-300 flex flex-col"
              >
                <div className="w-11 h-11 rounded-xl bg-[#f37721]/10 flex items-center justify-center mb-4">
                  <Icon className="w-5 h-5 text-[#f37721]" strokeWidth={1.75} />
                </div>
                <h3 className="text-sm font-semibold text-[#311b52] mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  {cat.title}
                </h3>
                <p className="text-xs text-[#3e195e]/70 leading-relaxed flex-1" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  {examplesText(cat.examples)}
                </p>
              </motion.div>
            );
          })}
        </div>

        <div className="text-center">
          <Link
            to="/vaccination-catalogue"
            data-testid="catalogue-teaser-cta"
            className="group inline-flex items-center gap-2 text-sm font-semibold text-[#311b52] border-b-2 border-[#f37721] pb-1 hover:text-[#f37721] transition-colors"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            {catalogue.cta_label || "View full catalogue"}
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </div>
    </section>
  );
}
